import SEO, { Schema } from "@/components/SEO";
import Navigation from "@/components/navigation";
import HeroSection from "@/components/hero-section";
import TrustIndicators from "@/components/trust-indicators";
import PropertySearch from "@/components/property-search";
import Footer from "@/components/footer";
import { HomeSEO } from "@/components/SEO";
import { Link } from "wouter";
import { Waves, MapPin, TrendingUp, Award } from "lucide-react";

export default function Home() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Selling South FL Waterfront | Luxury Waterfront Homes & Condos 2025"
        description="Explore South Florida's premier waterfront properties. Luxury homes, condos, and estates in Miami, Fort Lauderdale, and Boca Raton. Interactive IDX map search with 100+ listings."
        keywords={[
          "south florida waterfront real estate",
          "luxury homes miami",
          "fort lauderdale condos",
          "boca raton waterfront estates",
          "intracoastal homes for sale",
          "miami beach oceanfront condos",
        ]}
      />
      <Schema
        type="RealEstateAgent"
        data={{
          name: "Selling South FL Waterfront",
          url: "https://sellingsouthflwaterfront.com",
          logo: "https://sellingsouthflwaterfront.com/logo.png",
          image: "https://sellingsouthflwaterfront.com/og-image.jpg",
          description:
            "Premier luxury waterfront real estate agency specializing in South Florida's most exclusive properties.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "South Florida",
            addressRegion: "FL",
            addressCountry: "US",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: "26.1224",
            longitude: "-80.1373",
          },
          areaServed: [
            "Miami",
            "Fort Lauderdale",
            "Boca Raton",
            "Miami Beach",
            "Sunny Isles Beach",
            "Hollywood",
            "Pompano Beach",
            "Hallandale Beach",
          ],
          priceRange: "$$$",
        }}
      />
      <Navigation />
      <HeroSection />
      <TrustIndicators />
      
      {/* Featured Properties - Show FIRST so users see listings immediately */}
      <PropertySearch />

      {/* SEO-Rich Content Section - After Property Listings */}
      <section className="py-16 px-4 bg-gradient-to-br from-blue-50 via-white to-cyan-50" itemScope itemType="https://schema.org/Service">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose South Florida Waterfront Living
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Discover South Florida's premier destination for luxury waterfront real estate. From oceanfront condos in Miami Beach to bayfront estates in Fort Lauderdale and canal homes in Boca Raton, explore exceptional waterfront properties across Miami-Dade, Broward, and Palm Beach counties.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <Waves className="w-12 h-12 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Exclusive Waterfront Properties
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Explore Fort Lauderdale luxury homes for sale featuring deepwater docks and direct ocean access, browse Miami's most prestigious oceanfront condos with panoramic Atlantic views, or discover Boca Raton's exclusive waterfront estates in premier gated communities. Our portfolio includes 100+ handpicked waterfront listings ranging from $500,000 beachfront condos to $50 million+ ultra-luxury oceanfront compounds.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <MapPin className="w-12 h-12 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Prime South Florida Locations
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    From Miami Beach's iconic South Beach and trendy Brickell Bay to Fort Lauderdale's prestigious Las Olas Isles and Harbor Beach, and Boca Raton's exclusive Royal Palm Yacht & Country Club, we cover every major waterfront community. Search by neighborhood, waterway access, or lifestyle preferences to find your perfect coastal property in South Florida's most desirable zip codes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <TrendingUp className="w-12 h-12 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Smart Waterfront Investment
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    South Florida waterfront real estate offers exceptional investment potential with 6-9% annual appreciation, strong rental income opportunities, and Florida's unbeatable tax advantages including no state income tax. Whether you're seeking a primary residence, vacation retreat, or investment property, our experts provide market analysis, rental projections, and financing guidance to maximize your waterfront investment returns.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg">
              <div className="flex items-start gap-4">
                <Award className="w-12 h-12 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    Expert Local Knowledge
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Our team specializes exclusively in waterfront properties, bringing deep expertise in yacht dockage requirements, seawall conditions, flood insurance, and hurricane protection. We guide clients through every aspect of waterfront home ownership, from understanding Intracoastal bridge clearances to navigating HOA regulations, ensuring your purchase decision is fully informed and confident.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Additional SEO Keywords Section */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              Comprehensive Waterfront Property Services Across South Florida
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Miami Waterfront Real Estate:</strong> Discover luxury oceanfront condos Miami Beach, bayfront penthouses Brickell, intracoastal homes Coconut Grove, and exclusive island estates Key Biscayne. Our Miami waterfront specialists know every building, every neighborhood, and every market trend to help you find the perfect property whether you're seeking a $600K starter condo or a $20M+ estate.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              <strong>Fort Lauderdale Waterfront Homes:</strong> Explore Fort Lauderdale's 165 miles of scenic waterways featuring no-bridge ocean access properties Las Olas Isles, exclusive oceanfront estates Harbor Beach, deepwater canal homes Coral Ridge, and charming intracoastal properties Victoria Park. Fort Lauderdale offers superior boating lifestyle with quick ocean access, world-class marinas, and the annual Fort Lauderdale International Boat Show.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Boca Raton Luxury Properties:</strong> Browse Boca Raton's most prestigious waterfront communities including Royal Palm Yacht & Country Club, Boca Raton Resort & Club residences, exclusive oceanfront condos, and prime intracoastal estates. Boca Raton combines luxury waterfront living with A-rated schools, world-class shopping, and sophisticated country club lifestyle, making it ideal for families and retirees seeking the best of South Florida.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Section - Last Section Before Footer */}
      <section className="py-16 px-4 bg-gradient-to-br from-slate-50 via-white to-blue-50" itemScope itemType="https://schema.org/Blog">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Expert Waterfront Real Estate Insights
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover proven strategies for buying luxury waterfront homes, market trends, investment analysis, and insider tips from South Florida real estate experts.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            <Link href="/blog/best-waterfront-condos-miami-2025">
              <a className="block bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-4">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                    Miami Market
                  </span>
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-3 leading-tight">
                  Best Waterfront Condos Miami 2025
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Top luxury buildings and market analysis for Miami's most prestigious waterfront condos
                </p>
                <span className="text-blue-600 font-semibold text-sm hover:underline inline-flex items-center gap-2">
                  Read Article →
                </span>
              </a>
            </Link>
            
            <Link href="/blog/fort-lauderdale-luxury-homes-for-sale">
              <a className="block bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-4">
                  <span className="inline-block bg-cyan-100 text-cyan-800 text-xs font-semibold px-3 py-1 rounded-full">
                    Fort Lauderdale
                  </span>
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-3 leading-tight">
                  Fort Lauderdale Luxury Homes Guide
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Neighborhood spotlights, buying tips, and exclusive waterfront communities
                </p>
                <span className="text-blue-600 font-semibold text-sm hover:underline inline-flex items-center gap-2">
                  Read Article →
                </span>
              </a>
            </Link>
            
            <Link href="/blog/waterfront-investment-guide-south-florida">
              <a className="block bg-white rounded-xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="mb-4">
                  <span className="inline-block bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                    Investment Guide
                  </span>
                </div>
                <h3 className="font-bold text-xl text-gray-900 mb-3 leading-tight">
                  Waterfront Investment Guide
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  ROI analysis, tax strategies, and rental income projections for waterfront properties
                </p>
                <span className="text-blue-600 font-semibold text-sm hover:underline inline-flex items-center gap-2">
                  Read Article →
                </span>
              </a>
            </Link>
          </div>
          
          <div className="text-center">
            <Link href="/blog">
              <a className="inline-flex items-center gap-2 bg-blue-600 text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                View All Articles
                <span className="text-xl">→</span>
              </a>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
